/**
 * Phase-3 — Client-inputs API client (agency employee surface).
 *
 * The "Client inbox": incoming questions / next-month requests raised by a
 * brand's portal clients, answered + resolved by the agency. Thin hand-written
 * fetch wrapper matching client-reports-api.ts:
 *   - Bearer = the agency `emp_` key from Redux (`auth.agency.accessToken`).
 *   - `credentials: 'include'` so the httpOnly agency-session cookie also
 *     authenticates on a cold reload.
 *   - Base URL `CENTRAL_API_BASE_URL` (= `${env}/api/v1/`).
 *   - Enriched `Error & { status, code, reason }`.
 *
 * NOTE: unlike the Laravel `{message,errors}` envelope, this API's
 * validation-failure envelope is CUSTOM: `{success:false, message:"…"}` (422).
 * Access failures carry their own shapes: 403 `{reason:"brand_access"}`,
 * 404 (cross-org / unknown shop or thread).
 *
 * Backend contract (central-api, branch feat/report-phase3-client-inputs;
 * gated require_role: can:portal.clients.manage — owner/manager/team_lead;
 * org + brand scoped — do NOT change it here):
 *   GET  client-inputs?shop_slug={slug}&status={open|answered|resolved}?
 *          → 200 { inputs: ClientInput[] }
 *          missing shop_slug → 422; cross-org/unknown shop → 404;
 *          in-org but out-of-brand-scope → 403 {reason:"brand_access"}.
 *   POST client-inputs/{id}/reply {body:string<=4000}
 *          → 200 { input }; sets status='answered'; emails the client.
 *          cross-org id → 404; out-of-scope → 403.
 *   POST client-inputs/{id}/resolve
 *          → 200 { input }; sets status='resolved'.
 */
import { CENTRAL_API_BASE_URL } from '../../constants/central-api';
import store from '../../../store/store';

// ---------- wire types ----------

export type ClientInputType = 'question' | 'next_month_request';

export type ClientInputStatus = 'open' | 'answered' | 'resolved';

// Wire values from central-api ClientInputReply::toAgencyArray: 'client' | 'agency'.
export type ClientInputReplyAuthor = 'client' | 'agency' | string;

export interface ClientInputReply {
  author_type: ClientInputReplyAuthor;
  /** Only exposed on the AGENCY surface (never on the client portal). */
  author_id: string | null;
  body: string;
  created_at: string | null;
}

export interface ClientInputClient {
  email: string;
  name: string | null;
}

export interface ClientInput {
  id: string;
  shop_id: number | string | null;
  client_login_id: string | null;
  type: ClientInputType | string;
  status: ClientInputStatus | string;
  /** Free-form <=120 chars, e.g. the report title the client is asking about. */
  context: string | null;
  body: string;
  report_id: string | null;
  client: ClientInputClient | null;
  created_at: string | null;
  updated_at: string | null;
  replies: ClientInputReply[];
}

export interface ClientInputsApiError extends Error {
  status?: number;
  code?: string;
  /** 403 payloads carry `{reason:"brand_access"}`. */
  reason?: string;
}

// ---------- internal helpers (mirrors client-reports-api) ----------

function getAuthHeader(): Record<string, string> {
  const token = store.getState().auth.agency.accessToken;
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

async function parseJson<T>(response: Response): Promise<T> {
  const text = await response.text();
  if (!text) return {} as T;
  try {
    return JSON.parse(text) as T;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[client-inputs-api] failed to parse JSON', err, text);
    throw new Error('Invalid response from server.');
  }
}

async function throwEnriched(response: Response): Promise<never> {
  let detail = '';
  let code: string | undefined;
  let reason: string | undefined;
  try {
    const body = await parseJson<{
      message?: string;
      error?: string;
      reason?: string;
    }>(response);
    code = body?.error;
    reason = body?.reason;
    detail = body?.message || body?.error || body?.reason || '';
  } catch {
    /* fall back to status text */
  }
  const err = new Error(
    detail || `Request failed (${response.status} ${response.statusText})`
  ) as ClientInputsApiError;
  err.status = response.status;
  err.code = code;
  err.reason = reason;
  throw err;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    ...getAuthHeader(),
    ...((init.headers as Record<string, string>) || {}),
  };
  if (init.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  const response = await fetch(`${CENTRAL_API_BASE_URL}${path}`, {
    ...init,
    headers,
    credentials: 'include',
  });
  if (!response.ok) await throwEnriched(response);
  return parseJson<T>(response);
}

function toQuery(params: Record<string, string | undefined>): string {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== '' && v !== null) usp.append(k, String(v));
  });
  const q = usp.toString();
  return q ? `?${q}` : '';
}

// ---------- endpoints ----------

export interface ListClientInputsParams {
  shopSlug: string;
  status?: ClientInputStatus;
}

/**
 * GET client-inputs?shop_slug=&status= — the brand's client inbox. `shop_slug`
 * is required (422 otherwise). `status` filters to a single bucket; omit for
 * all statuses.
 */
export async function listClientInputs(
  params: ListClientInputsParams
): Promise<ClientInput[]> {
  const res = await request<{ inputs?: ClientInput[] }>(
    `client-inputs${toQuery({
      shop_slug: params.shopSlug,
      status: params.status,
    })}`,
    { method: 'GET' }
  );
  return res.inputs ?? [];
}

/**
 * POST client-inputs/{id}/reply — post an agency reply on a thread. Sets the
 * thread status='answered' and emails the client a branded magic-link deep
 * link (?next=/inputs/{id}). Returns the updated thread.
 */
export async function replyToClientInput(
  id: string,
  body: string
): Promise<ClientInput> {
  const res = await request<{ input: ClientInput }>(
    `client-inputs/${encodeURIComponent(id)}/reply`,
    { method: 'POST', body: JSON.stringify({ body }) }
  );
  return res.input;
}

/**
 * POST client-inputs/{id}/resolve — mark the thread resolved. Returns the
 * updated thread.
 */
export async function resolveClientInput(id: string): Promise<ClientInput> {
  const res = await request<{ input: ClientInput }>(
    `client-inputs/${encodeURIComponent(id)}/resolve`,
    { method: 'POST' }
  );
  return res.input;
}
